import { useState, useCallback } from 'react';

/**
 * Export a DOM node to a multi-page A4 PDF.
 *
 * html2canvas rasterizes the whole node (so Leaflet tiles and SVG donuts are
 * captured as pixels); the tall canvas is then sliced across A4 pages. Loads
 * the heavy libs lazily so they don't bloat the initial bundle.
 */
export function useExportPdf() {
  const [exporting, setExporting] = useState(false);

  const exportPdf = useCallback(async (node: HTMLElement, filename: string) => {
    setExporting(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f6f7f9',
        logging: false,
        // Skip controls marked .no-export (e.g. the export button itself).
        ignoreElements: (el) => el.classList?.contains('no-export') ?? false,
        // Leaflet panes use transforms html2canvas mishandles unless we let it
        // render at the natural scroll size.
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;

      let heightLeft = imgH;
      let position = 0;
      const imgData = canvas.toDataURL('image/jpeg', 0.92);

      pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
      heightLeft -= pageH;

      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
        heightLeft -= pageH;
      }

      pdf.save(filename);
    } finally {
      setExporting(false);
    }
  }, []);

  return { exportPdf, exporting };
}
